import { useState, useEffect, useMemo } from "react";
import { loansService, installmentsService } from "../services/api";
import {
  formatCurrency,
  toPersianNumbers,
  formatJalaliDate,
} from "../utils/numbers";
import {
  Calendar as CalendarIcon,
  Check,
  CheckCircle2,
  Clock,
  X,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Search,
  User,
  Banknote,
  Eye,
  RefreshCw,
  Layers,
  FileText,
  CreditCard,
  XCircle,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { compressImage } from "../utils/imageCompressor";
import { SearchableSelect } from "../components/SearchableSelect";

// Helper to compare Jalali/Gregorian dates in YYYY/MM/DD or YYYY-MM-DD formats
const compareRequestedDates = (
  dateA?: string,
  dateB?: string,
  direction: "asc" | "desc" = "asc",
) => {
  if (!dateA && !dateB) return 0;
  if (!dateA) return 1;
  if (!dateB) return -1;

  const parseParts = (d: string) => {
    const parts = d.split(/[\/\-]/).map((p) => parseInt(p, 10));
    if (parts.length === 3 && parts.every((n) => !isNaN(n))) {
      return parts[0] * 10000 + parts[1] * 100 + parts[2];
    }
    return null;
  };

  const numA = parseParts(dateA);
  const numB = parseParts(dateB);

  let diff = 0;
  if (numA !== null && numB !== null) {
    diff = numA - numB;
  } else {
    const timeA = new Date(dateA).getTime();
    const timeB = new Date(dateB).getTime();
    if (!isNaN(timeA) && !isNaN(timeB)) {
      diff = timeA - timeB;
    } else {
      diff = String(dateA).localeCompare(String(dateB));
    }
  }

  return direction === "asc" ? diff : -diff;
};

const getImageUrl = (url: string | undefined) => {
  if (!url) return "";
  const baseUrl = (import.meta as any).env?.VITE_API_BASE_URL || "";
  if (baseUrl && url.startsWith("/api/")) {
    return baseUrl.replace(/\/api\/?$/, "") + url;
  }
  return url;
};

interface Installment {
  id: number;
  installment_code: string;
  amount: string | number;
  due_date: string;
  status: "pending" | "paid" | "overdue";
  penalty_amount?: number;
  paid_date?: string;
  receipt_image?: string;
}

interface Loan {
  id: number;
  loan_code?: string;
  user_id?: number;
  amount: string | number;
  requested_date: string;
  created_at?: string;
  description?: string;
  collateral?: string;
  status:
    | "pending"
    | "waiting_user_approval"
    | "user_approved"
    | "approved_registration"
    | "fee_paid"
    | "paid"
    | "rejected"
    | "cancelled";
  loan_type_name?: string;
  percentage?: number;
  installments_count?: number;
  requested_percentage?: number;
  requested_installments_count?: number;
  registration_fee_amount?: string | number;
  registration_fee_percentage?: number;
  receipt_image?: string;
  is_fee_paid?: boolean;
  admin_notes?: string;
  user_notes?: string;
  negotiation_history?: string;
  first_name?: string;
  last_name?: string;
  national_code?: string;
  mobile?: string;
  user_role?: string;
  installments?: Installment[];
  is_settled?: boolean;
}

const isLoanSettled = (loan: Loan): boolean => {
  return (
    loan.is_settled === true ||
    (loan.status === "paid" &&
      Boolean(loan.installments && loan.installments.length > 0) &&
      loan.installments!.every((i) => i.status === "paid"))
  );
};

interface StageInfo {
  stepNumber: number;
  totalSteps: number;
  stageTitle: string;
  stageDescription: string;
  nextAction: string;
  badgeBg: string;
  badgeText: string;
  badgeBorder: string;
  stepIndex: number; // 0 to 6, or -1 for rejected/cancelled
}

const getStageInfo = (
  status: Loan["status"],
  isSettled: boolean = false,
): StageInfo => {
  if (isSettled) {
    return {
      stepNumber: 7,
      totalSteps: 7,
      stageTitle: "مرحله ۷: تسویه شده کامل",
      stageDescription:
        "تمامی اقساط این وام به طور کامل پرداخت و با صندوق تسویه شده است. این پرونده به صورت دائمی در سوابق و تاریخچه وام‌ها حفظ می‌شود.",
      nextAction: "پرونده تسویه و مختومه شده است (ماندگار در تاریخچه)",
      badgeBg: "bg-emerald-50 dark:bg-emerald-950/40",
      badgeText: "text-emerald-700 dark:text-emerald-300",
      badgeBorder: "border-emerald-300 dark:border-emerald-700",
      stepIndex: 6,
    };
  }

  switch (status) {
    case "pending":
      return {
        stepNumber: 1,
        totalSteps: 7,
        stageTitle: "مرحله ۱: ثبت اولیه درخواست",
        stageDescription:
          "درخواست توسط متقاضی ثبت شده و در صف بررسی اولیه توسط مدیریت است.",
        nextAction: "بررسی درخواست و تعیین شرایط پیشنهادی توسط مدیریت",
        badgeBg: "bg-amber-50 dark:bg-amber-950/40",
        badgeText: "text-amber-700 dark:text-amber-300",
        badgeBorder: "border-amber-200 dark:border-amber-800",
        stepIndex: 0,
      };
    case "waiting_user_approval":
      return {
        stepNumber: 2,
        totalSteps: 7,
        stageTitle: "مرحله ۲: پیشنهاد شرایط مدیر",
        stageDescription:
          "مدیریت شرایط وام (درصد کارمزد و تعداد اقساط) را تعیین کرده و منتظر تایید متقاضی است.",
        nextAction:
          "ورود متقاضی به بخش وام‌ها جهت پذیرش شرایط یا ارسال پیشنهاد متقابل",
        badgeBg: "bg-purple-50 dark:bg-purple-950/40",
        badgeText: "text-purple-700 dark:text-purple-300",
        badgeBorder: "border-purple-200 dark:border-purple-800",
        stepIndex: 1,
      };
    case "user_approved":
      return {
        stepNumber: 3,
        totalSteps: 7,
        stageTitle: "مرحله ۳: تایید شرایط توسط متقاضی",
        stageDescription:
          "شرایط پیشنهادی توسط متقاضی پذیرفته شده و در انتظار تایید ثبت‌نام اولیه توسط مدیر است.",
        nextAction: "بررسی و تایید ثبت‌نام اولیه در بخش تایید ثبت‌نام مدیریت",
        badgeBg: "bg-cyan-50 dark:bg-cyan-950/40",
        badgeText: "text-cyan-700 dark:text-cyan-300",
        badgeBorder: "border-cyan-200 dark:border-cyan-800",
        stepIndex: 2,
      };
    case "approved_registration":
      return {
        stepNumber: 4,
        totalSteps: 7,
        stageTitle: "مرحله ۴: تایید ثبت‌نام اولیه",
        stageDescription:
          "ثبت‌نام اولیه تایید شده؛ متقاضی باید کارمزد ثبت‌نام را واریز و فیش را بارگذاری کند.",
        nextAction: "واریز مبلغ کارمزد ثبت‌نام و آپلود فیش واریزی توسط متقاضی",
        badgeBg: "bg-emerald-50 dark:bg-emerald-950/40",
        badgeText: "text-emerald-700 dark:text-emerald-300",
        badgeBorder: "border-emerald-200 dark:border-emerald-800",
        stepIndex: 3,
      };
    case "fee_paid":
      return {
        stepNumber: 5,
        totalSteps: 7,
        stageTitle: "مرحله ۵: کارمزد تسویه و تایید شد",
        stageDescription:
          "کارمزد ثبت‌نام اولیه واریز و توسط مدیریت تایید شده است. وام آماده پرداخت است.",
        nextAction: "تخصیص نهایی وجه وام، واریز به حساب متقاضی و تقسیط وام",
        badgeBg: "bg-indigo-50 dark:bg-indigo-950/40",
        badgeText: "text-indigo-700 dark:text-indigo-300",
        badgeBorder: "border-indigo-200 dark:border-indigo-800",
        stepIndex: 4,
      };
    case "paid":
      return {
        stepNumber: 6,
        totalSteps: 7,
        stageTitle: "مرحله ۶: در حال بازپرداخت اقساط",
        stageDescription:
          "مبلغ وام پرداخت گردیده و اقساط ماهانه در جریان بازپرداخت هستند.",
        nextAction: "بازپرداخت منظم اقساط ماهانه طبق سررسید",
        badgeBg: "bg-blue-50 dark:bg-blue-950/40",
        badgeText: "text-blue-700 dark:text-blue-300",
        badgeBorder: "border-blue-200 dark:border-blue-800",
        stepIndex: 5,
      };
    case "rejected":
      return {
        stepNumber: 0,
        totalSteps: 7,
        stageTitle: "رد شده توسط مدیریت",
        stageDescription: "این درخواست وام توسط مدیریت صندوق رد شده است.",
        nextAction: "درخواست مختومه است",
        badgeBg: "bg-red-50 dark:bg-red-950/40",
        badgeText: "text-red-700 dark:text-red-300",
        badgeBorder: "border-red-200 dark:border-red-800",
        stepIndex: -1,
      };
    case "cancelled":
      return {
        stepNumber: 0,
        totalSteps: 7,
        stageTitle: "لغو شده توسط متقاضی",
        stageDescription: "این درخواست وام توسط خود متقاضی لغو گردیده است.",
        nextAction: "درخواست لغو و مختومه شده است",
        badgeBg: "bg-slate-100 dark:bg-slate-800",
        badgeText: "text-slate-600 dark:text-slate-400",
        badgeBorder: "border-slate-200 dark:border-slate-700",
        stepIndex: -1,
      };
    default:
      return {
        stepNumber: 1,
        totalSteps: 7,
        stageTitle: "در انتظار بررسی",
        stageDescription: "در حال بررسی و پردازش در سامانه",
        nextAction: "پیگیری بررسی اداری",
        badgeBg: "bg-slate-100 dark:bg-slate-800",
        badgeText: "text-slate-700 dark:text-slate-300",
        badgeBorder: "border-slate-200 dark:border-slate-700",
        stepIndex: 0,
      };
  }
};

const getCollateralLabel = (val?: string) => {
  switch (val) {
    case "gold":
      return "طلا";
    case "simcard":
      return "سیم کارت";
    case "dollar":
      return "دلار";
    case "document":
      return "سند ملکی";
    case "credit":
      return "اعتبار صندوق";
    default:
      return val || "ثبت نشده";
  }
};

const WORKFLOW_STEPS = [
  { step: 1, key: "pending", title: "ثبت اولیه", shortDesc: "بررسی مدیر" },
  {
    step: 2,
    key: "waiting_user_approval",
    title: "پیشنهاد شرایط",
    shortDesc: "تایید متقاضی",
  },
  {
    step: 3,
    key: "user_approved",
    title: "تایید متقاضی",
    shortDesc: "تایید ثبت‌نام",
  },
  {
    step: 4,
    key: "approved_registration",
    title: "تایید ثبت‌نام",
    shortDesc: "واریز کارمزد",
  },
  {
    step: 5,
    key: "fee_paid",
    title: "تسویه کارمزد",
    shortDesc: "در صف پرداخت",
  },
  { step: 6, key: "paid", title: "پرداخت وام", shortDesc: "بازپرداخت اقساط" },
  { step: 7, key: "settled", title: "تسویه کامل", shortDesc: "پایان اقساط" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "همه پرونده‌ها (شامل وام‌های تسویه شده)" },
  { value: "settled", label: "وام‌های تسویه شده (اتمام کامل اقساط)" },
  { value: "active_repayment", label: "وام‌های فعال در حال بازپرداخت اقساط" },
  { value: "pending", label: "مرحله ۱: ثبت اولیه درخواست (بررسی مدیر)" },
  {
    value: "waiting_user_approval",
    label: "مرحله ۲: پیشنهاد شرایط مدیر (تایید متقاضی)",
  },
  {
    value: "user_approved",
    label: "مرحله ۳: تایید شرایط متقاضی (تایید ثبت‌نام)",
  },
  {
    value: "approved_registration",
    label: "مرحله ۴: تایید ثبت‌نام اولیه (واریز کارمزد)",
  },
  { value: "fee_paid", label: "مرحله ۵: تسویه کارمزد (در صف پرداخت وام)" },
  { value: "paid", label: "مرحله ۶: کل وام‌های پرداخت شده (فعال و تسویه)" },
  { value: "cancelled_or_rejected", label: "همه درخواست‌های لغو یا رد شده" },
  { value: "rejected", label: "رد شده توسط مدیریت" },
  { value: "cancelled", label: "لغو شده توسط متقاضی" },
];

const getRegistrationFeeInfo = (loan: Loan) => {
  let feeAmount = Number(loan.registration_fee_amount || 0);
  const feePerc =
    loan.registration_fee_percentage != null
      ? Number(loan.registration_fee_percentage)
      : null;
  if ((!feeAmount || feeAmount === 0) && feePerc && Number(loan.amount) > 0) {
    feeAmount = (Number(loan.amount) * feePerc) / 100;
  }
  return { feeAmount, feePerc };
};

export default function LoanHistory() {
  const [loans, setLoans] = useState<Loan[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedLoanId, setExpandedLoanId] = useState<number | null>(null);

  // Admin filter states
  const [activeTab, setActiveTab] = useState<"all" | "my">("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Modals
  const [viewReceiptModalLoan, setViewReceiptModalLoan] = useState<Loan | null>(
    null,
  );
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedInstId, setSelectedInstId] = useState<number | null>(null);
  const [receiptImageFile, setReceiptImageFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [cancelingLoanId, setCancelingLoanId] = useState<number | null>(null);
  const [isCanceling, setIsCanceling] = useState(false);

  // Current user info
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin =
    user?.role === "admin" ||
    user?.role === "investor" ||
    (user?.permissions &&
      (user.permissions.includes("admin") ||
        user.permissions.includes("loan-reservations") ||
        user.permissions.includes("loan-payments") ||
        user.permissions.includes("loan-history")));

  useEffect(() => {
    fetchLoans();
  }, [isAdmin, activeTab, sortOrder]);

  const fetchLoans = async () => {
    setLoading(true);
    try {
      if (isAdmin) {
        if (activeTab === "my") {
          const data = await loansService.getMyLoans();
          setLoans(Array.isArray(data) ? data : []);
        } else {
          // Fetch all loans for admin sorted by requested_date
          const data = await loansService.getLoans({
            sort: "requested_date",
            order: sortOrder,
          });
          setLoans(Array.isArray(data) ? data : []);
        }
      } else {
        // Regular user: ONLY their own loans
        const data = await loansService.getMyLoans();
        setLoans(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error("Error fetching loans:", error);
      setLoans([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const handleCancelLoan = async () => {
    if (!cancelingLoanId) return;
    setIsCanceling(true);
    try {
      await loansService.cancelLoan(cancelingLoanId);
      setIsCancelModalOpen(false);
      setCancelingLoanId(null);
      fetchLoans();
    } catch (e: any) {
      alert(e?.response?.data?.message || "خطا در لغو درخواست وام");
    } finally {
      setIsCanceling(false);
    }
  };

  const getInstallmentStatusBadge = (status: Installment["status"]) => {
    switch (status) {
      case "paid":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400">
            پرداخت شده
          </span>
        );
      case "overdue":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400">
            دارای دیرکرد
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
            در انتظار پرداخت
          </span>
        );
    }
  };

  // Filtered and sorted loans list
  const filteredLoans = useMemo(() => {
    const list = loans.filter((loan) => {
      // Status filter
      if (selectedStatus !== "all") {
        if (selectedStatus === "settled") {
          if (!isLoanSettled(loan)) return false;
        } else if (selectedStatus === "active_repayment") {
          if (loan.status !== "paid" || isLoanSettled(loan)) return false;
        } else if (selectedStatus === "cancelled_or_rejected") {
          if (loan.status !== "cancelled" && loan.status !== "rejected")
            return false;
        } else if (loan.status !== selectedStatus) {
          return false;
        }
      }

      // Search query filter (for applicant name, national code, mobile, loan code)
      if (searchQuery.trim()) {
        const q = searchQuery.trim().toLowerCase();
        const fullName =
          `${loan.first_name || ""} ${loan.last_name || ""}`.toLowerCase();
        const nationalCode = (loan.national_code || "").toLowerCase();
        const mobile = (loan.mobile || "").toLowerCase();
        const loanCode = (loan.loan_code || "").toLowerCase();
        const amountStr = String(loan.amount || "");

        return (
          fullName.includes(q) ||
          nationalCode.includes(q) ||
          mobile.includes(q) ||
          loanCode.includes(q) ||
          amountStr.includes(q)
        );
      }

      return true;
    });

    // Sort strictly by requested_date based on sortOrder (tiebreaker by id)
    return list.sort((a, b) => {
      const dateDiff = compareRequestedDates(
        a.requested_date,
        b.requested_date,
        sortOrder,
      );
      if (dateDiff !== 0) return dateDiff;
      return sortOrder === "asc" ? a.id - b.id : b.id - a.id;
    });
  }, [loans, selectedStatus, searchQuery, sortOrder]);

  // Calculate statistics for admin
  const totalCount = loans.length;
  const inProgressCount = loans.filter((l) =>
    ["pending", "waiting_user_approval", "user_approved"].includes(l.status),
  ).length;
  const feePaidCount = loans.filter((l) =>
    ["approved_registration", "fee_paid"].includes(l.status),
  ).length;
  const settledCount = loans.filter((l) => isLoanSettled(l)).length;
  const activePaidCount = loans.filter(
    (l) => l.status === "paid" && !isLoanSettled(l),
  ).length;

  return (
    <div className="space-y-6 pb-12">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-slate-800 dark:text-white flex items-center gap-3">
            <span className="p-2 rounded-xl bg-persian-50 text-persian-600 dark:bg-persian-900/30 dark:text-persian-400">
              <Layers className="w-6 h-6" />
            </span>
            {isAdmin ? "مدیریت و تاریخچه جامع وام‌ها" : "تاریخچه وام‌های من"}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {isAdmin
              ? "مشاهده تمامی درخواست‌های ثبت‌شده در سامانه، وضعیت گام‌به‌گام و مرحله دقیق هر پرونده"
              : "پیگیری وضعیت درخواست‌های وام، مرحله جاری و سوابق اقساط بازپرداخت"}
          </p>
        </div>

        <button
          onClick={fetchLoans}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors self-start sm:self-auto shadow-sm"
        >
          <RefreshCw className="w-4 h-4" />
          بروزرسانی داده‌ها
        </button>
      </div>

      {/* Admin Stats & View Mode Tabs */}
      {isAdmin && (
        <div className="space-y-4">
          {/* Quick Metrics */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-xs">
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">
                کل پرونده‌ها
              </p>
              <div className="flex items-baseline justify-between">
                <span className="text-2xl font-black text-slate-900 dark:text-white">
                  {toPersianNumbers(totalCount)}
                </span>
                <span className="text-xs text-slate-400">وام</span>
              </div>
            </div>

            <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-amber-100 dark:border-amber-900/30 shadow-xs">
              <p className="text-xs text-amber-600 dark:text-amber-400 mb-1">
                در جریان بررسی و تایید
              </p>
              <div className="flex items-baseline justify-between">
                <span className="text-2xl font-black text-amber-600 dark:text-amber-400">
                  {toPersianNumbers(inProgressCount)}
                </span>
                <span className="text-xs text-amber-600/70">مرحله ۱ تا ۳</span>
              </div>
            </div>

            <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-indigo-100 dark:border-indigo-900/30 shadow-xs">
              <p className="text-xs text-indigo-600 dark:text-indigo-400 mb-1">
                کارمزد و در صف پرداخت
              </p>
              <div className="flex items-baseline justify-between">
                <span className="text-2xl font-black text-indigo-600 dark:text-indigo-400">
                  {toPersianNumbers(feePaidCount)}
                </span>
                <span className="text-xs text-indigo-600/70">مرحله ۴ و ۵</span>
              </div>
            </div>

            <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-blue-100 dark:border-blue-900/30 shadow-xs">
              <p className="text-xs text-blue-600 dark:text-blue-400 mb-1">
                در حال بازپرداخت
              </p>
              <div className="flex items-baseline justify-between">
                <span className="text-2xl font-black text-blue-600 dark:text-blue-400">
                  {toPersianNumbers(activePaidCount)}
                </span>
                <span className="text-xs text-blue-600/70">اقساط جاری</span>
              </div>
            </div>

            <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-emerald-200 dark:border-emerald-900/40 shadow-xs bg-gradient-to-br from-emerald-50/40 to-white dark:from-emerald-950/20 dark:to-slate-800">
              <p className="text-xs text-emerald-700 dark:text-emerald-300 font-bold mb-1 flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                <span>وام‌های تسویه شده</span>
              </p>
              <div className="flex items-baseline justify-between">
                <span className="text-2xl font-black text-emerald-700 dark:text-emerald-400">
                  {toPersianNumbers(settledCount)}
                </span>
                <span className="text-xs text-emerald-600 font-medium">
                  باقی در تاریخچه
                </span>
              </div>
            </div>
          </div>

          {/* Tab Selector & Filter Bar */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-4 border border-slate-200 dark:border-slate-700 shadow-xs flex flex-col lg:flex-row gap-4 justify-between items-stretch lg:items-center">
            {/* View Mode: All Loans vs My Own */}
            <div className="inline-flex p-1 bg-slate-100 dark:bg-slate-700/50 rounded-xl self-start">
              <button
                onClick={() => setActiveTab("all")}
                className={`px-4 py-2 text-sm font-bold rounded-lg transition-all ${
                  activeTab === "all"
                    ? "bg-white dark:bg-slate-800 text-persian-600 dark:text-persian-400 shadow-xs"
                    : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                }`}
              >
                تمام وام‌های متقاضیان ({toPersianNumbers(loans.length)})
              </button>
              <button
                onClick={() => setActiveTab("my")}
                className={`px-4 py-2 text-sm font-bold rounded-lg transition-all ${
                  activeTab === "my"
                    ? "bg-white dark:bg-slate-800 text-persian-600 dark:text-persian-400 shadow-xs"
                    : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                }`}
              >
                وام‌های شخصی من
              </button>
            </div>

            {/* Search & Stage Filter */}
            <div className="flex flex-wrap items-center gap-3 flex-1 lg:max-w-2xl justify-end">
              {/* Search */}
              <div className="relative flex-1 min-w-[200px]">
                <Search className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="جستجو متقاضی، کد ملی، همراه، کد وام..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pr-9 pl-4 py-2 rounded-xl text-sm bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 focus:outline-hidden focus:ring-2 focus:ring-persian-500 text-slate-800 dark:text-slate-100"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Status Filter using SearchableSelect */}
              <div className="w-full sm:w-[280px]">
                <SearchableSelect
                  options={STATUS_OPTIONS}
                  value={selectedStatus}
                  onChange={(val) => setSelectedStatus(val || "all")}
                  placeholder="انتخاب وضعیت یا مرحله..."
                />
              </div>

              {/* Sort Order Toggle */}
              <button
                type="button"
                onClick={toggleSortOrder}
                className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 hover:bg-slate-100 dark:bg-slate-900/50 dark:hover:bg-slate-800 text-xs font-bold text-slate-700 dark:text-slate-200 transition-colors shadow-xs"
                title="تغییر ترتیب مرتب‌سازی بر اساس تاریخ درخواست"
              >
                {sortOrder === "asc" ? (
                  <>
                    <ArrowUp className="w-4 h-4 text-persian-600 dark:text-persian-400" />
                    <span>تاریخ درخواست: صعودی (قدیمی به جدید)</span>
                  </>
                ) : (
                  <>
                    <ArrowDown className="w-4 h-4 text-persian-600 dark:text-persian-400" />
                    <span>تاریخ درخواست: نزولی (جدید به قدیمی)</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Regular User Filter Bar */}
      {!isAdmin && (
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-4 border border-slate-200 dark:border-slate-700 shadow-xs flex flex-col sm:flex-row gap-4 justify-between items-stretch sm:items-center">
          <div className="flex flex-wrap items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-200">
            <span>
              تعداد کل پرونده‌های من: {toPersianNumbers(loans.length)} وام
            </span>
            {settledCount > 0 && (
              <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800 flex items-center gap-1">
                <Check className="w-3 h-3 text-emerald-600" />
                <span>
                  {toPersianNumbers(settledCount)} وام تسویه شده (باقی‌مانده در
                  سوابق)
                </span>
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3 flex-1 sm:max-w-2xl justify-end">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="جستجو کد وام یا مبلغ..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pr-9 pl-4 py-2 rounded-xl text-sm bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 focus:outline-hidden focus:ring-2 focus:ring-persian-500 text-slate-800 dark:text-slate-100"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="w-full sm:w-[260px]">
              <SearchableSelect
                options={STATUS_OPTIONS}
                value={selectedStatus}
                onChange={(val) => setSelectedStatus(val || "all")}
                placeholder="انتخاب وضعیت یا مرحله..."
              />
            </div>
            {/* User Sort Order Toggle */}
            <button
              type="button"
              onClick={toggleSortOrder}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 hover:bg-slate-100 dark:bg-slate-900/50 dark:hover:bg-slate-800 text-xs font-bold text-slate-700 dark:text-slate-200 transition-colors shadow-xs"
              title="تغییر ترتیب مرتب‌سازی بر اساس تاریخ درخواست"
            >
              {sortOrder === "asc" ? (
                <>
                  <ArrowUp className="w-4 h-4 text-persian-600 dark:text-persian-400" />
                  <span>تاریخ: صعودی</span>
                </>
              ) : (
                <>
                  <ArrowDown className="w-4 h-4 text-persian-600 dark:text-persian-400" />
                  <span>تاریخ: نزولی</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      {loading ? (
        <div className="flex flex-col items-center justify-center h-[45vh] bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700">
          <div className="w-10 h-10 border-4 border-persian-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
            در حال بارگذاری اطلاعات پرونده‌های وام...
          </p>
        </div>
      ) : filteredLoans.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-xs border border-slate-200 dark:border-slate-700 p-12 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-700 text-slate-400 mb-4">
            <AlertCircle className="w-8 h-8" />
          </div>
          <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
            هیچ وامی یافت نشد
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto">
            {searchQuery || selectedStatus !== "all"
              ? "با فیلترهای انتخابی هیچ موردی یافت نشد. لطفاً فیلترها را تغییر دهید."
              : isAdmin
                ? "هنوز هیچ درخواست وامی در سامانه ثبت نشده است."
                : "شما تاکنون درخواست وامی ثبت نکرده‌اید."}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {filteredLoans.map((loan) => {
            const isSettled = isLoanSettled(loan);
            const stage = getStageInfo(loan.status, isSettled);
            const feeInfo = getRegistrationFeeInfo(loan);
            const isExpanded = expandedLoanId === loan.id;
            const hasInstallments =
              loan.installments && loan.installments.length > 0;
            const isOwner =
              user?.id === loan.user_id ||
              (user?.national_code &&
                loan.national_code === user.national_code);
            const canCancel =
              (isOwner || isAdmin) &&
              [
                "pending",
                "waiting_user_approval",
                "user_approved",
                "approved_registration",
              ].includes(loan.status);

            return (
              <div
                key={loan.id}
                className="bg-white dark:bg-slate-800 rounded-3xl shadow-xs border border-slate-200 dark:border-slate-700 overflow-hidden transition-all duration-200 hover:border-slate-300 dark:hover:border-slate-600"
              >
                {/* Header Summary Row */}
                <div
                  onClick={() => setExpandedLoanId(isExpanded ? null : loan.id)}
                  className={`p-5 sm:p-6 cursor-pointer transition-colors ${
                    isExpanded
                      ? "bg-slate-50/70 dark:bg-slate-900/40 border-b border-slate-200 dark:border-slate-700"
                      : "hover:bg-slate-50/50 dark:hover:bg-slate-700/20"
                  }`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    {/* Applicant & Basic Data */}
                    <div className="flex flex-wrap items-center gap-4 sm:gap-6">
                      {/* Loan Code */}
                      <div>
                        <span className="text-xs text-slate-400 block mb-0.5 font-medium">
                          کد وام
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-base font-black text-slate-900 dark:text-white">
                            {loan.loan_code ? (
                              <bdi dir="ltr" className="inline-block font-bold">
                                {toPersianNumbers(loan.loan_code)}
                              </bdi>
                            ) : (
                              `وام #${toPersianNumbers(loan.id)}`
                            )}
                          </span>
                          {isSettled && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/80 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700">
                              <Check className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                              <span>تسویه شده (باقی‌مانده در سوابق)</span>
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="hidden sm:block w-px h-8 bg-slate-200 dark:bg-slate-700"></div>

                      {/* Applicant info for admin */}
                      {isAdmin && (
                        <>
                          <div>
                            <span className="text-xs text-slate-400 block mb-0.5 font-medium">
                              متقاضی
                            </span>
                            <div className="flex items-center gap-1.5 text-sm font-bold text-slate-900 dark:text-white">
                              <User className="w-3.5 h-3.5 text-persian-500 shrink-0" />
                              <span>
                                {loan.first_name || ""}{" "}
                                {loan.last_name || "کاربر"}
                              </span>
                              {loan.national_code && (
                                <span className="text-xs font-normal text-slate-500 dark:text-slate-400">
                                  ({toPersianNumbers(loan.national_code)})
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="hidden sm:block w-px h-8 bg-slate-200 dark:bg-slate-700"></div>
                        </>
                      )}

                      {/* Amount */}
                      <div>
                        <span className="text-xs text-slate-400 block mb-0.5 font-medium">
                          مبلغ وام
                        </span>
                        <span className="text-base font-black text-persian-600 dark:text-persian-400">
                          {formatCurrency(loan.amount)}
                        </span>
                      </div>

                      <div className="hidden sm:block w-px h-8 bg-slate-200 dark:bg-slate-700"></div>

                      {/* Percentage (درصد کارمزد وام) */}
                      <div>
                        <span className="text-xs text-slate-400 block mb-0.5 font-medium">
                          درصد کارمزد
                        </span>
                        <div className="flex items-center gap-1">
                          <span className="text-base font-black text-slate-900 dark:text-white">
                            {loan.percentage !== undefined &&
                            loan.percentage !== null
                              ? `${toPersianNumbers(loan.percentage)}٪`
                              : loan.requested_percentage !== undefined &&
                                  loan.requested_percentage !== null
                                ? `${toPersianNumbers(loan.requested_percentage)}٪`
                                : "-"}
                          </span>
                          {loan.percentage == null &&
                            loan.requested_percentage != null && (
                              <span className="text-[10px] text-amber-600 bg-amber-50 dark:bg-amber-950/40 px-1 py-0.5 rounded font-medium">
                                درخواستی
                              </span>
                            )}
                        </div>
                      </div>

                      <div className="hidden sm:block w-px h-8 bg-slate-200 dark:bg-slate-700"></div>

                      {/* Registration Fee (کارمزد ثبت اولیه) */}
                      <div>
                        <span className="text-xs text-slate-400 block mb-0.5 font-medium">
                          کارمزد ثبت اولیه
                        </span>
                        <div className="flex items-center gap-1.5">
                          <span className="text-base font-black text-amber-700 dark:text-amber-400">
                            {feeInfo.feeAmount > 0
                              ? formatCurrency(feeInfo.feeAmount)
                              : feeInfo.feePerc != null && feeInfo.feePerc > 0
                                ? `${toPersianNumbers(feeInfo.feePerc)}٪`
                                : "۰ ریال"}
                          </span>
                          <span
                            className={`text-[10px] px-1.5 py-0.5 rounded-md font-bold ${
                              loan.is_fee_paid ||
                              ["fee_paid", "paid"].includes(loan.status)
                                ? "bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-300 dark:border-emerald-800"
                                : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400"
                            }`}
                          >
                            {loan.is_fee_paid ||
                            ["fee_paid", "paid"].includes(loan.status)
                              ? "تسویه شده"
                              : "پرداخت نشده"}
                          </span>
                        </div>
                      </div>

                      <div className="hidden sm:block w-px h-8 bg-slate-200 dark:bg-slate-700"></div>

                      {/* Installments Count (تعداد اقساط) */}
                      <div>
                        <span className="text-xs text-slate-400 block mb-0.5 font-medium">
                          تعداد اقساط
                        </span>
                        <div className="flex items-center gap-1">
                          <span className="text-base font-black text-slate-900 dark:text-white">
                            {loan.installments_count
                              ? `${toPersianNumbers(loan.installments_count)} قسط`
                              : loan.requested_installments_count
                                ? `${toPersianNumbers(loan.requested_installments_count)} قسط`
                                : "-"}
                          </span>
                          {!loan.installments_count &&
                            loan.requested_installments_count && (
                              <span className="text-[10px] text-amber-600 bg-amber-50 dark:bg-amber-950/40 px-1 py-0.5 rounded font-medium">
                                درخواستی
                              </span>
                            )}
                        </div>
                      </div>

                      <div className="hidden sm:block w-px h-8 bg-slate-200 dark:bg-slate-700"></div>

                      {/* Loan Type */}
                      {loan.loan_type_name && (
                        <>
                          <div className="hidden md:block">
                            <span className="text-xs text-slate-400 block mb-0.5 font-medium">
                              نوع وام
                            </span>
                            <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                              {loan.loan_type_name}
                            </span>
                          </div>
                          <div className="hidden md:block w-px h-8 bg-slate-200 dark:bg-slate-700"></div>
                        </>
                      )}

                      {/* Requested Date */}
                      <div>
                        <span className="text-xs text-slate-400 block mb-0.5 font-medium">
                          تاریخ درخواست
                        </span>
                        <div className="flex items-center gap-1 text-sm font-medium text-slate-700 dark:text-slate-300">
                          <CalendarIcon className="w-3.5 h-3.5 text-slate-400" />
                          <span dir="ltr">
                            {formatJalaliDate(loan.requested_date)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Stage Badge & Actions */}
                    <div className="flex items-center gap-3 self-end lg:self-auto flex-wrap">
                      {/* Cancel Button */}
                      {canCancel && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setCancelingLoanId(loan.id);
                            setIsCancelModalOpen(true);
                          }}
                          className="px-3 py-1.5 text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 dark:bg-red-950/40 dark:text-red-400 dark:hover:bg-red-900/50 rounded-xl transition-colors border border-red-200 dark:border-red-900/40"
                        >
                          لغو درخواست
                        </button>
                      )}

                      {/* Stage Badge with Step Number */}
                      <div
                        className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl border text-xs font-bold ${stage.badgeBg} ${stage.badgeText} ${stage.badgeBorder}`}
                      >
                        {isSettled ? (
                          <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
                        ) : loan.status === "paid" ? (
                          <CheckCircle2 className="w-4 h-4 shrink-0 text-blue-500" />
                        ) : loan.status === "rejected" ||
                          loan.status === "cancelled" ? (
                          <XCircle className="w-4 h-4 shrink-0 text-red-500" />
                        ) : (
                          <Clock className="w-4 h-4 shrink-0 text-amber-500" />
                        )}
                        <span>{stage.stageTitle}</span>
                      </div>

                      {/* Expand / Collapse Button */}
                      <div className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5" />
                        ) : (
                          <ChevronDown className="w-5 h-5" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Stage Progress Stepper Bar (Visual Workflow) */}
                  <div className="mt-5 pt-4 border-t border-slate-100 dark:border-slate-700/60">
                    <div className="flex items-center justify-between mb-2 flex-wrap sm:flex-nowrap gap-2">
                      <span className="text-xs font-bold text-slate-700 dark:text-slate-300 flex items-center gap-1.5 truncate">
                        <span className="w-2 h-2 rounded-full bg-persian-500 shrink-0"></span>
                        <span className="truncate">
                          مرحله پرونده: {stage.stageTitle}
                        </span>
                      </span>
                      <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400 shrink-0 whitespace-nowrap">
                        {stage.stepIndex >= 0
                          ? `گام ${toPersianNumbers(stage.stepIndex + 1)} از ۷`
                          : "وضعیت خاتمه‌یافته"}
                      </span>
                    </div>

                    {/* Horizontal Step Indicator - 7 Steps in a Single Line */}
                    {stage.stepIndex >= 0 ? (
                      <div
                        className="grid grid-cols-7 gap-1 sm:gap-2 pt-1 w-full"
                        style={{
                          gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                        }}
                      >
                        {WORKFLOW_STEPS.map((ws, idx) => {
                          const isCompleted = idx < stage.stepIndex;
                          const isCurrent = idx === stage.stepIndex;
                          return (
                            <div
                              key={ws.key}
                              className="flex flex-col gap-1 min-w-0"
                            >
                              <div
                                className={`h-2 rounded-full transition-all ${
                                  isCompleted
                                    ? "bg-emerald-500 dark:bg-emerald-400"
                                    : isCurrent
                                      ? "bg-persian-600 dark:bg-persian-400 animate-pulse"
                                      : "bg-slate-200 dark:bg-slate-700"
                                }`}
                              />
                              <div className="flex flex-col text-center min-w-0">
                                <span
                                  className={`text-[9px] sm:text-[11px] leading-tight font-bold truncate ${
                                    isCurrent
                                      ? "text-persian-600 dark:text-persian-400"
                                      : isCompleted
                                        ? "text-emerald-600 dark:text-emerald-400"
                                        : "text-slate-400 dark:text-slate-500"
                                  }`}
                                  title={`${ws.title} (${ws.shortDesc})`}
                                >
                                  {ws.title}
                                </span>
                                <span className="hidden md:inline-block text-[9px] text-slate-400 truncate mt-0.5">
                                  {ws.shortDesc}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      /* Special banner for cancelled/rejected */
                      <div className="p-2.5 rounded-xl bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/30 flex items-center gap-2 text-xs text-red-700 dark:text-red-400">
                        <AlertCircle className="w-4 h-4 shrink-0" />
                        <span>{stage.stageDescription}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Expanded Details Panel */}
                {isExpanded && (
                  <div className="p-6 bg-slate-50/50 dark:bg-slate-900/30 space-y-6 border-t border-slate-100 dark:border-slate-800">
                    {/* Stage Explanation & Next Action Alert Box */}
                    <div className="p-4 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-persian-600 dark:text-persian-400">
                            توضیحات این مرحله:
                          </span>
                          <span className="text-xs text-slate-700 dark:text-slate-300">
                            {stage.stageDescription}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-slate-500 dark:text-slate-400">
                            اقدام بعدی لازم:
                          </span>
                          <span className="text-xs text-amber-700 dark:text-amber-400 font-medium">
                            {stage.nextAction}
                          </span>
                        </div>
                      </div>

                      {/* Fee Receipt Preview Button if uploaded */}
                      {loan.receipt_image && (
                        <button
                          type="button"
                          onClick={() => setViewReceiptModalLoan(loan)}
                          className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-950/40 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 rounded-xl transition-colors border border-indigo-200 dark:border-indigo-800 shrink-0 self-start sm:self-auto"
                        >
                          <Eye className="w-4 h-4" />
                          مشاهده فیش واریزی کارمزد
                        </button>
                      )}
                    </div>

                    {/* Detailed Information Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {/* Box 1: Applicant Information */}
                      <div className="p-4 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 space-y-2">
                        <h5 className="text-xs font-bold text-slate-400 flex items-center gap-1.5 border-b border-slate-100 dark:border-slate-700/60 pb-2">
                          <User className="w-3.5 h-3.5 text-persian-500" />
                          مشخصات متقاضی
                        </h5>
                        <div className="space-y-1.5 text-xs">
                          <div className="flex justify-between">
                            <span className="text-slate-500 dark:text-slate-400">
                              نام و نشان:
                            </span>
                            <span className="font-bold text-slate-800 dark:text-slate-200">
                              {loan.first_name || ""} {loan.last_name || "-"}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500 dark:text-slate-400">
                              کد ملی:
                            </span>
                            <span className="font-bold text-slate-800 dark:text-slate-200">
                              {loan.national_code
                                ? toPersianNumbers(loan.national_code)
                                : "-"}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500 dark:text-slate-400">
                              شماره تماس:
                            </span>
                            <span
                              className="font-bold text-slate-800 dark:text-slate-200"
                              dir="ltr"
                            >
                              {loan.mobile
                                ? toPersianNumbers(loan.mobile)
                                : "-"}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Box 2: Loan Financial Terms */}
                      <div className="p-4 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 space-y-2">
                        <h5 className="text-xs font-bold text-slate-400 flex items-center gap-1.5 border-b border-slate-100 dark:border-slate-700/60 pb-2">
                          <Banknote className="w-3.5 h-3.5 text-persian-500" />
                          شرایط تسهیلات
                        </h5>
                        <div className="space-y-1.5 text-xs">
                          <div className="flex justify-between">
                            <span className="text-slate-500 dark:text-slate-400">
                              مبلغ اصل وام:
                            </span>
                            <span className="font-black text-persian-600 dark:text-persian-400">
                              {formatCurrency(loan.amount)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500 dark:text-slate-400">
                              درصد کارمزد وام:
                            </span>
                            <span className="font-bold text-slate-800 dark:text-slate-200">
                              {loan.percentage !== undefined &&
                              loan.percentage !== null
                                ? `${toPersianNumbers(loan.percentage)}٪`
                                : loan.requested_percentage !== undefined &&
                                    loan.requested_percentage !== null
                                  ? `${toPersianNumbers(loan.requested_percentage)}٪ (درخواستی)`
                                  : "-"}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500 dark:text-slate-400">
                              تعداد اقساط:
                            </span>
                            <span className="font-bold text-slate-800 dark:text-slate-200">
                              {loan.installments_count
                                ? `${toPersianNumbers(loan.installments_count)} قسط (ماهه)`
                                : loan.requested_installments_count
                                  ? `${toPersianNumbers(loan.requested_installments_count)} قسط (درخواستی)`
                                  : "-"}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Box 3: Collateral & Registration Fee */}
                      <div className="p-4 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 space-y-2">
                        <h5 className="text-xs font-bold text-slate-400 flex items-center gap-1.5 border-b border-slate-100 dark:border-slate-700/60 pb-2">
                          <CreditCard className="w-3.5 h-3.5 text-persian-500" />
                          وثیقه و کارمزد ثبت‌نام اولیه
                        </h5>
                        <div className="space-y-1.5 text-xs">
                          <div className="flex justify-between">
                            <span className="text-slate-500 dark:text-slate-400">
                              نوع وثیقه:
                            </span>
                            <span className="font-bold text-slate-800 dark:text-slate-200">
                              {getCollateralLabel(loan.collateral)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500 dark:text-slate-400">
                              کارمزد ثبت اولیه:
                            </span>
                            <span className="font-bold text-amber-700 dark:text-amber-400">
                              {feeInfo.feeAmount > 0
                                ? formatCurrency(feeInfo.feeAmount)
                                : "۰ ریال"}
                              {feeInfo.feePerc != null &&
                                feeInfo.feePerc > 0 && (
                                  <span className="text-[11px] text-slate-400 mr-1">
                                    ({toPersianNumbers(feeInfo.feePerc)}٪)
                                  </span>
                                )}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500 dark:text-slate-400">
                              وضعیت کارمزد:
                            </span>
                            <span
                              className={`font-bold ${
                                loan.is_fee_paid ||
                                ["fee_paid", "paid"].includes(loan.status)
                                  ? "text-emerald-600 dark:text-emerald-400"
                                  : "text-amber-600 dark:text-amber-400"
                              }`}
                            >
                              {loan.is_fee_paid ||
                              ["fee_paid", "paid"].includes(loan.status)
                                ? "تسویه و تایید شده"
                                : "در انتظار پرداخت"}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Box 4: Dates & Negotiation History */}
                      <div className="p-4 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 space-y-2">
                        <h5 className="text-xs font-bold text-slate-400 flex items-center gap-1.5 border-b border-slate-100 dark:border-slate-700/60 pb-2">
                          <CalendarIcon className="w-3.5 h-3.5 text-persian-500" />
                          تاریخ‌ها و پیشنهاد اولیه
                        </h5>
                        <div className="space-y-1.5 text-xs">
                          <div className="flex justify-between">
                            <span className="text-slate-500 dark:text-slate-400">
                              تاریخ درخواست:
                            </span>
                            <span
                              className="font-bold text-slate-800 dark:text-slate-200"
                              dir="ltr"
                            >
                              {formatJalaliDate(loan.requested_date)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500 dark:text-slate-400">
                              اقساط درخواستی اولیه:
                            </span>
                            <span className="font-bold text-slate-800 dark:text-slate-200">
                              {loan.requested_installments_count
                                ? `${toPersianNumbers(loan.requested_installments_count)} ماه`
                                : "-"}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500 dark:text-slate-400">
                              کارمزد درخواستی اولیه:
                            </span>
                            <span className="font-bold text-slate-800 dark:text-slate-200">
                              {loan.requested_percentage !== undefined &&
                              loan.requested_percentage !== null
                                ? `${toPersianNumbers(loan.requested_percentage)}٪`
                                : "-"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Descriptions and Notes */}
                    {(loan.description ||
                      loan.admin_notes ||
                      loan.user_notes) && (
                      <div className="p-4 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 space-y-2 text-xs">
                        {loan.description && (
                          <div className="flex flex-col sm:flex-row gap-1 sm:gap-2">
                            <span className="font-bold text-slate-500 shrink-0">
                              توضیحات متقاضی:
                            </span>
                            <span className="text-slate-800 dark:text-slate-200">
                              {loan.description}
                            </span>
                          </div>
                        )}
                        {loan.admin_notes && (
                          <div className="flex flex-col sm:flex-row gap-1 sm:gap-2">
                            <span className="font-bold text-persian-600 dark:text-persian-400 shrink-0">
                              یادداشت مدیر:
                            </span>
                            <span className="text-slate-800 dark:text-slate-200">
                              {loan.admin_notes}
                            </span>
                          </div>
                        )}
                        {loan.user_notes && (
                          <div className="flex flex-col sm:flex-row gap-1 sm:gap-2">
                            <span className="font-bold text-amber-600 dark:text-amber-400 shrink-0">
                              یادداشت متقاضی در مذاکره:
                            </span>
                            <span className="text-slate-800 dark:text-slate-200">
                              {loan.user_notes}
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Installments Table */}
                    {hasInstallments && (
                      <div className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-5 space-y-3">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
                            <CreditCard className="w-4 h-4 text-persian-600" />
                            جدول و سوابق اقساط بازپرداخت (
                            {toPersianNumbers(loan.installments!.length)} قسط)
                          </h4>
                        </div>

                        <div className="overflow-x-auto">
                          <table className="w-full text-right text-xs">
                            <thead>
                              <tr className="text-slate-400 border-b border-slate-100 dark:border-slate-700/80">
                                <th className="pb-3 font-semibold">ردیف</th>
                                <th className="pb-3 font-semibold">
                                  شناسه قسط
                                </th>
                                <th className="pb-3 font-semibold">مبلغ قسط</th>
                                <th className="pb-3 font-semibold">
                                  تاریخ سررسید
                                </th>
                                <th className="pb-3 font-semibold">
                                  تاریخ پرداخت
                                </th>
                                <th className="pb-3 font-semibold">دیرکرد</th>
                                <th className="pb-3 font-semibold">وضعیت</th>
                                <th className="pb-3 font-semibold">
                                  فیش پرداختی
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                              {loan.installments!.map((installment, index) => (
                                <tr
                                  key={installment.id}
                                  className="text-slate-700 dark:text-slate-300"
                                >
                                  <td className="py-3 font-medium">
                                    {toPersianNumbers(index + 1)}
                                  </td>
                                  <td className="py-3 font-bold text-slate-900 dark:text-white">
                                    <bdi
                                      dir="ltr"
                                      className="inline-block font-bold"
                                    >
                                      {toPersianNumbers(
                                        installment.installment_code,
                                      )}
                                    </bdi>
                                  </td>
                                  <td className="py-3 font-bold text-persian-600 dark:text-persian-400">
                                    {formatCurrency(installment.amount)}
                                  </td>
                                  <td className="py-3" dir="ltr">
                                    {formatJalaliDate(installment.due_date)}
                                  </td>
                                  <td className="py-3" dir="ltr">
                                    {installment.paid_date ? (
                                      formatJalaliDate(installment.paid_date)
                                    ) : (
                                      <span className="text-slate-400">-</span>
                                    )}
                                  </td>
                                  <td className="py-3">
                                    {installment.penalty_amount &&
                                    Number(installment.penalty_amount) > 0 ? (
                                      <span className="text-red-500 font-bold">
                                        {formatCurrency(
                                          installment.penalty_amount,
                                        )}
                                      </span>
                                    ) : (
                                      <span className="text-slate-400">-</span>
                                    )}
                                  </td>
                                  <td className="py-3">
                                    {getInstallmentStatusBadge(
                                      installment.status,
                                    )}
                                  </td>
                                  <td className="py-3">
                                    {installment.status === "pending" ||
                                    installment.status === "overdue" ? (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedInstId(installment.id);
                                          setReceiptImageFile(null);
                                          setIsUploadModalOpen(true);
                                        }}
                                        className="text-xs bg-persian-50 hover:bg-persian-100 text-persian-600 dark:bg-persian-900/30 dark:text-persian-400 px-3 py-1.5 rounded-lg transition-colors font-bold whitespace-nowrap border border-persian-200 dark:border-persian-800"
                                      >
                                        {installment.receipt_image
                                          ? "ارسال مجدد فیش"
                                          : "ارسال فیش"}
                                      </button>
                                    ) : installment.receipt_image ? (
                                      <span className="text-xs text-emerald-600 dark:text-emerald-400 font-bold whitespace-nowrap flex items-center gap-1">
                                        <Check className="w-3.5 h-3.5" />
                                        فیش ثبت شد
                                      </span>
                                    ) : (
                                      <span className="text-xs text-slate-400 whitespace-nowrap">
                                        -
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Settled Loan Status Banner */}
                    {isSettled && (
                      <div className="p-4 bg-emerald-50/90 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/60 rounded-2xl flex items-center gap-3 text-xs text-emerald-900 dark:text-emerald-200 shadow-xs">
                        <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                        <div>
                          <span className="font-bold text-sm block">
                            این پرونده وام به طور کامل با صندوق تسویه شده است.
                          </span>
                          <span className="text-slate-600 dark:text-slate-300 text-xs">
                            کلیه اقساط پرداخت گردیده و اطلاعات این وام به عنوان
                            سابقه مالی شما به صورت دائمی در تاریخچه وام‌ها حفظ
                            می‌شود و حذف نخواهد شد.
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* View Fee Receipt Modal */}
      {viewReceiptModalLoan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-700 w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
            <div className="p-5 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center bg-slate-50 dark:bg-slate-900/40">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                <h3 className="text-base font-bold text-slate-900 dark:text-white">
                  فیش واریزی کارمزد ثبت‌نام اولیه
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setViewReceiptModalLoan(null)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 p-1 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-4">
              {/* Loan and applicant info summary */}
              <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 text-xs space-y-1.5">
                <div className="flex justify-between">
                  <span className="text-slate-500">متقاضی:</span>
                  <span className="font-bold text-slate-800 dark:text-slate-200">
                    {viewReceiptModalLoan.first_name}{" "}
                    {viewReceiptModalLoan.last_name} (
                    {toPersianNumbers(viewReceiptModalLoan.national_code)})
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">مبلغ کارمزد ثبت‌نام:</span>
                  <span className="font-bold text-indigo-600 dark:text-indigo-400">
                    {formatCurrency(
                      viewReceiptModalLoan.registration_fee_amount,
                    )}
                  </span>
                </div>
              </div>

              {/* Receipt Image Display */}
              <div className="rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-900/80 flex items-center justify-center min-h-[220px]">
                {viewReceiptModalLoan.receipt_image ? (
                  <img
                    src={getImageUrl(viewReceiptModalLoan.receipt_image)}
                    alt="فیش واریزی"
                    className="max-h-[50vh] w-auto object-contain mx-auto"
                  />
                ) : (
                  <p className="text-sm text-slate-400">تصویری موجود نیست</p>
                )}
              </div>
            </div>

            <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 flex justify-end">
              <button
                type="button"
                onClick={() => setViewReceiptModalLoan(null)}
                className="px-5 py-2.5 rounded-xl bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-800 dark:text-slate-200 font-bold text-sm transition-colors"
              >
                بستن
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Loan Modal */}
      {isCancelModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-700 w-full max-w-sm overflow-hidden">
            <div className="p-6 border-b border-slate-200 dark:border-slate-700 bg-red-50 dark:bg-red-950/20 flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
              <h3 className="text-lg font-black text-red-900 dark:text-red-300">
                لغو درخواست وام
              </h3>
            </div>

            <div className="p-6 space-y-6">
              <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                آیا از لغو این پرونده درخواست وام اطمینان دارید؟ با تایید لغو،
                وضعیت وام به «لغو شده» تغییر می‌یابد.
              </p>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleCancelLoan}
                  disabled={isCanceling}
                  className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white h-11 rounded-xl font-bold text-sm transition-colors flex items-center justify-center gap-2 shadow-xs"
                >
                  {isCanceling && (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  )}
                  {isCanceling ? "در حال لغو..." : "بله، لغو شود"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsCancelModalOpen(false);
                    setCancelingLoanId(null);
                  }}
                  disabled={isCanceling}
                  className="flex-1 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 h-11 rounded-xl font-bold text-sm transition-colors"
                >
                  انصراف
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload Installment Receipt Modal */}
      {isUploadModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-700 w-full max-w-md overflow-hidden">
            <div className="p-6 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
              <h3 className="text-base font-bold text-slate-900 dark:text-white">
                ارسال فیش واریزی قسط
              </h3>
              <button
                type="button"
                onClick={() => setIsUploadModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">
                  انتخاب تصویر فیش واریز (JPG, PNG)
                </label>
                <input
                  type="file"
                  accept="image/jpeg, image/png"
                  onChange={(e) =>
                    setReceiptImageFile(e.target.files?.[0] || null)
                  }
                  className="block w-full text-sm text-slate-500
                    file:mr-4 file:py-2.5 file:px-4
                    file:rounded-xl file:border-0
                    file:text-xs file:font-bold
                    file:bg-persian-50 file:text-persian-700
                    hover:file:bg-persian-100
                    dark:file:bg-persian-900/30 dark:file:text-persian-400 cursor-pointer"
                />
              </div>

              <div className="pt-2 flex gap-3">
                <button
                  type="button"
                  onClick={async () => {
                    if (!receiptImageFile || !selectedInstId) return;
                    setIsUploading(true);
                    try {
                      const base64Image = await compressImage(
                        receiptImageFile,
                        800,
                        800,
                        0.7,
                      );
                      await installmentsService.uploadInstallmentReceipt(
                        selectedInstId,
                        base64Image,
                      );
                      setIsUploadModalOpen(false);
                      fetchLoans();
                    } catch (err) {
                      alert("خطا در ارسال تصویر فیش");
                    } finally {
                      setIsUploading(false);
                    }
                  }}
                  disabled={!receiptImageFile || isUploading}
                  className="flex-1 bg-persian-600 hover:bg-persian-700 disabled:opacity-50 text-white h-11 rounded-xl font-bold text-sm transition-colors flex items-center justify-center gap-2"
                >
                  {isUploading && (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  )}
                  {isUploading ? "در حال ارسال فیش..." : "ارسال و ثبت فیش"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsUploadModalOpen(false);
                    setReceiptImageFile(null);
                  }}
                  disabled={isUploading}
                  className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-slate-200 h-11 rounded-xl font-bold text-sm transition-colors"
                >
                  انصراف
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
